import { useContext, useEffect, useState } from 'react';
import Scrollbar from 'src/components/Scrollbar';
import { SidebarContext } from 'src/contexts/SidebarContext';
import {
  Box,
  Button,
  Drawer,
  IconButton,
  styled,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { NavLink as RouterLink, useNavigate } from 'react-router-dom';
import SidebarMenu from './SidebarMenu';
import { versionName } from 'src/appConfig';
import {
  hireUs,
  hireUsLink,
  hireUsLogo,
  launchIcon,
  seoAcademy,
  seoAcademyLogo,
  seoAcademyLink,
  sidebarTexts,
  paths,
  localSeoLogo
} from 'src/common/config/TextMessage';
import {
  isLocalSeoUser,
  localStorageMethod,
  openNewTab
} from 'src/common/config/FunctionHelper';
import { getHireUs, getSeoAcademy } from 'src/common/Scraper';
import { LocalSeoIconSx, sidebarSx } from 'src/common/CommomSxs';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/redux/store';
import { setIsOpenChangePlan } from 'src/redux/siteAuditReducer';
import CloseIcon from '@mui/icons-material/Close';
/**
 * SidebarWrapper is a styled Box component that serves as the wrapper for the sidebar.
 *
 * It defines the styling and layout properties of the sidebar container.
 */
const SidebarWrapper = styled(Box)(
  ({ theme }) => `
  padding: 10px 0px 10px;
  gap: 10px;
  position: relative;
  width: 250px;
  height: 100%;
  left: 0px;
  background: var(--color-white);
  display: flex;
  flex-direction: column;
`
);

/**
 * Sidebar component responsible for navigation and user-related actions.
 */
const Sidebar = () => {
  const { sidebarToggle, toggleSidebar, closeSidebar: closeSidebarContext } =
    useContext(SidebarContext);
  const [hide, setHide] = useState(true);
  let subscriptionDetails =
    useSelector(
      (state: RootState) => state.counter?.userDetails?.['subscription']
    ) || null;
  const subscription: any = useSelector(
    (state: RootState) => state.user.subscription
  );
  let userDetails: any =
    useSelector((state: RootState) => state.counter.userDetails) ?? {};
  let activePlanName = useSelector((state: RootState) => state.user.activePlan);
  let planDetails = useSelector((state: RootState) => state.user.planDetails);
  const isFreePlan = activePlanName === planDetails?.free;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const dismissUpgrade = localStorageMethod('get', 'dismissUpgrade') || null;
  const isNotPause = subscription && !subscription?.pause_collection?.behavior;
  let stripeSubscriptionDetails = useSelector(
    (state: RootState) => state.user.subscription
  );
  let isPremiumUser = useSelector((state: RootState) => state.user.premiumUser);

  const isNoPlanAccount =
    userDetails?.email &&
    userDetails?.temp == true &&
    (!stripeSubscriptionDetails?.subscription_id || !isPremiumUser);
  const isExpireAccount =
    !isFreePlan &&
    userDetails?.email &&
    userDetails?.temp !== true &&
    (!stripeSubscriptionDetails?.subscription_id || !isPremiumUser);

  const isNoPlanOrExpire = isNoPlanAccount || isExpireAccount;

  const isLocalSeo = isLocalSeoUser();
  const isLocalSeoNoPlanOrExpire = isLocalSeo && isNoPlanOrExpire;
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  /**
   * Automatically close the temporary mobile drawer when the screen transitions to desktop width.
   */
  useEffect(() => {
    if (isDesktop && sidebarToggle) {
      if (closeSidebarContext) {
        closeSidebarContext();
      }
    }
  }, [isDesktop, sidebarToggle, closeSidebarContext]);

  /**
   * Function to close the sidebar.
   */
  const closeSidebar = () => {
    if (closeSidebarContext) {
      closeSidebarContext();
    } else {
      toggleSidebar();
    }
  };
  return (
    <>
      {isDesktop && (
        <SidebarWrapper sx={sidebarSx.sidebarWrapper(theme)}>
          <Box sx={sidebarSx.sidebarMainbox}>
            <Scrollbar>
              <Box className="header-log-box">
                <Box>
                  <Button
                    disableRipple
                    disableElevation
                    disableFocusRipple
                    disableTouchRipple
                    component={RouterLink}
                    to={isFreePlan ? paths?.externalScans : paths?.dashboard}
                    sx={sidebarSx.logo}
                  >
                    <img
                      src={
                        isLocalSeo
                          ? localSeoLogo
                          : '/static/images/logo/new-logo.svg'
                      }
                      alt="SEOSpace"
                      style={isLocalSeo ? LocalSeoIconSx : {}}
                    />
                  </Button>
                </Box>
              </Box>
              <Box sx={sidebarSx.sidebarMenubox}>
                <SidebarMenu />
              </Box>
              {hide &&
                !dismissUpgrade &&
                subscriptionDetails &&
                (isFreePlan || isLocalSeo) &&
                isNotPause &&
                !isLocalSeoNoPlanOrExpire && (
                  <Box
                    sx={{
                      ...sidebarSx?.mainBox
                    }}
                  >
                    <Box sx={sidebarSx?.titleBoxSx}>
                      <Typography sx={sidebarSx?.unlockAllFeaturesSx}>
                        {isLocalSeo
                          ? sidebarTexts?.trySeo
                          : sidebarTexts?.unlockAllFeatures}
                      </Typography>
                      <IconButton
                        aria-label="close"
                        className="hover:rounded-lg"
                        sx={sidebarSx?.iconBtnSx}
                        onClick={() => {
                          setHide(false);
                          localStorageMethod('set', 'dismissUpgrade', 'true');
                        }}
                      >
                        <CloseIcon sx={sidebarSx.closeIcon} />
                      </IconButton>
                    </Box>
                    <Typography sx={sidebarSx?.upgradeDesc}>
                      {isLocalSeo
                        ? sidebarTexts?.trySeoDesc
                        : sidebarTexts?.upgradeDesc}
                    </Typography>
                    <Box sx={sidebarSx?.btnBox}>
                      <Button
                        onClick={() => {
                          if (isLocalSeo) {
                            navigate(paths?.trySeoSpace);
                          } else {
                            dispatch(setIsOpenChangePlan(true));
                            navigate(paths?.billing);
                          }
                        }}
                        variant="contained"
                        fullWidth
                        sx={sidebarSx?.btnSx}
                      >
                        <Typography sx={sidebarSx?.btnTextSx}>
                          {isLocalSeo
                            ? sidebarTexts?.ctaText
                            : sidebarTexts?.upgradePlan}
                        </Typography>
                      </Button>
                    </Box>
                  </Box>
                )}
            </Scrollbar>
          </Box>

          <Box sx={sidebarSx.sidebarFooter}>
            <Box className="hire-us">
              <Button
                startIcon={
                  <img style={{ width: 20, height: 20 }} src={hireUsLogo} />
                }
                endIcon={
                  <img style={{ width: 20, height: 20 }} src={launchIcon} />
                }
                variant="text"
                onClick={async () => {
                  let response = await getHireUs();
                  openNewTab(hireUsLink);
                }}
                sx={sidebarSx.hireUsButton}
              >
                <Typography sx={sidebarSx.hireUsBtnTypography}>
                  {hireUs}
                </Typography>
              </Button>
              {/* <Box className="hire-us"> */}
              <Button
                startIcon={
                  <img style={{ width: 20, height: 20 }} src={seoAcademyLogo} />
                }
                endIcon={
                  <img style={{ width: 20, height: 20 }} src={launchIcon} />
                }
                sx={sidebarSx.seoAcademyBtn}
                variant="text"
                onClick={async () => {
                  let response = await getSeoAcademy();
                  openNewTab(seoAcademyLink);
                }}
              >
                <Typography sx={sidebarSx.seoAcademyBtnTypography}>
                  {seoAcademy}
                </Typography>
              </Button>
            </Box>

            <Typography sx={sidebarSx.version}>{versionName}</Typography>
          </Box>
        </SidebarWrapper>
      )}
      {/** Mobile Menu */}
      {!isDesktop && (
        <Drawer
          sx={sidebarSx.mobileMenuDrawer(theme)}
          anchor={theme.direction === 'rtl' ? 'right' : 'left'}
          open={sidebarToggle}
          onClose={closeSidebar}
          variant="temporary"
          ModalProps={{
            keepMounted: true
          }}
          elevation={9}
        >
          <Box mt={3}>
            <Box mb={3} sx={sidebarSx.mobileLogoBox}>
              <Button
                disableRipple
                disableElevation
                disableFocusRipple
                disableTouchRipple
                component={RouterLink}
                to={isFreePlan ? paths?.externalScans : paths?.dashboard}
                sx={sidebarSx.logoBox}
              >
                <img
                  src={
                    isLocalSeo ? localSeoLogo : '/static/images/logo/new-logo.svg'
                  }
                  alt="SEOSpace"
                  style={isLocalSeo ? LocalSeoIconSx : {}}
                />
              </Button>
            </Box>
          </Box>
          <SidebarWrapper sx={{ background: 'var(--color-primary)' }}>
            <Box sx={sidebarSx.sidebarWrapperBox}>
              <Scrollbar>
                <SidebarMenu />
                {hide &&
                  !dismissUpgrade &&
                  subscriptionDetails &&
                  (isFreePlan || isLocalSeo) &&
                  isNotPause &&
                  !isLocalSeoNoPlanOrExpire && (
                    <Box sx={{ ...sidebarSx?.mainBox, marginTop: '8%' }}>
                      <Typography sx={sidebarSx?.unlockAllFeaturesSx}>
                        {isLocalSeo
                          ? sidebarTexts?.trySeo
                          : sidebarTexts?.unlockAllFeatures}
                      </Typography>
                      <Typography sx={sidebarSx?.upgradeDesc}>
                        {isLocalSeo
                          ? sidebarTexts?.trySeoDesc
                          : sidebarTexts?.upgradeDesc}
                      </Typography>
                      <Box sx={sidebarSx?.btnBox}>
                        <Typography
                          onClick={() => {
                            setHide(false);
                            localStorageMethod('set', 'dismissUpgrade', 'true');
                          }}
                          sx={sidebarSx?.dismiss}
                        >
                          {sidebarTexts?.dismiss}
                        </Typography>
                        <Typography
                          onClick={() => {
                            dispatch(setIsOpenChangePlan(true));
                            navigate(paths?.billing);
                          }}
                          sx={{
                            width: 'max-content',
                            ...sidebarSx?.upgradeText
                          }}
                        >
                          {isLocalSeo
                            ? sidebarTexts?.ctaText
                            : sidebarTexts?.upgradePlan}
                        </Typography>
                      </Box>
                    </Box>
                  )}
              </Scrollbar>
            </Box>

            <Box sx={sidebarSx.sidebarFooter}>
              <Box className="hire-us">
                <Button
                  startIcon={
                    <img style={{ width: 20, height: 20 }} src={hireUsLogo} />
                  }
                  endIcon={
                    <img style={{ width: 20, height: 20 }} src={launchIcon} />
                  }
                  variant="text"
                  onClick={async () => {
                    let response = await getHireUs();
                    openNewTab(hireUsLink);
                  }}
                  sx={sidebarSx.hireUsButton}
                >
                  <Typography sx={sidebarSx.hireUsBtnTypography}>
                    {hireUs}
                  </Typography>
                </Button>
                {/* <Box className="hire-us"> */}
                <Button
                  startIcon={
                    <img style={{ width: 20, height: 20 }} src={seoAcademyLogo} />
                  }
                  endIcon={
                    <img style={{ width: 20, height: 20 }} src={launchIcon} />
                  }
                  sx={sidebarSx.seoAcademyBtn}
                  variant="text"
                  onClick={async () => {
                    let response = await getSeoAcademy();
                    openNewTab(seoAcademyLink);
                  }}
                >
                  <Typography sx={sidebarSx.seoAcademyBtnTypography}>
                    {seoAcademy}
                  </Typography>
                </Button>
              </Box>
              <Typography sx={sidebarSx.version}>{versionName}</Typography>
            </Box>
          </SidebarWrapper>
        </Drawer>
      )}
    </>
  );
};

export default Sidebar;
